import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useFullScreen } from 'components/atoms/video/hooks/useFullScreen';
import type { TimeData, VideoPlayerRef } from '../video-player/VideoPlayer.data';

export default function useVideoActions(
  playerRef: RefObject<VideoPlayerRef | null>,
  removeOnPause: boolean,
  playing: boolean,
  muted: boolean,
): {
  progress: number;
  toggleMute: () => void;
  togglePlay: () => void;
  onProgressChange: (progress: number) => void;
  handleOnDuration: (durationInSeconds: number) => void;
  handleOnProgress: ({ seconds, duration, percent }: TimeData) => void;
  handleOnEnded: () => void;
  handleOnPlay: () => void;
  handleOnPause: () => void;
  duration: number;
  forcePlay: boolean;
  isPlaying: boolean;
  forceMute: boolean;
  isFullscreen: boolean;
  exitFullScreen: () => void;

  play: () => void;
  pause: () => void;
  toggleFullScreen: () => void;
  enterFullScreen: () => void;
} {
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [forcePlay, setForcePlay] = useState(playing);
  const [forceMute, setForceMute] = useState(muted);

  const { toggleFullScreen, enterFullScreen, exitFullScreen, isFullscreen } =
    useFullScreen(playerRef);

  useEffect(() => {
    setForceMute(muted);
  }, [muted]);

  useEffect(() => {
    if (playing || removeOnPause) {
      setForcePlay(playing);
    }
    if (playing) {
      playerRef.current?.play();
    } else {
      playerRef.current?.pause();
    }
  }, [playing, removeOnPause, playerRef]);

  const handleOnPause = useCallback(() => {
    setIsPlaying(false);
  }, []);
  const handleOnPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);
  const handleOnEnded = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }

    setForcePlay(false);
    playerRef.current?.pause();
  }, [playerRef]);

  const handleOnProgress = useCallback(({ percent }: TimeData) => {
    setProgress(percent);
  }, []);

  const handleOnDuration = useCallback((durationInSeconds: number) => {
    setDuration(durationInSeconds);
  }, []);

  const onProgressChange = useCallback(
    (progress: number) => {
      if (playerRef.current) {
        playerRef.current.seekTo(progress * playerRef.current.getDuration());
      }
    },
    [playerRef],
  );

  const pause = useCallback(() => {
    setForcePlay(false);
    playerRef.current?.pause();
  }, [playerRef]);

  const play = useCallback(() => {
    setForcePlay(true);
    playerRef.current?.play();
  }, [playerRef]);

  const togglePlay = useCallback(() => {
    if (!isPlaying || removeOnPause) {
      setForcePlay(!isPlaying);
    }
    if (!isPlaying) {
      playerRef.current?.play();
    } else {
      playerRef.current?.pause();
    }
  }, [isPlaying, removeOnPause, playerRef]);

  const toggleMute = useCallback(() => {
    setForceMute(!forceMute);
  }, [forceMute]);

  return {
    forceMute,
    isPlaying,
    progress,
    toggleMute,
    togglePlay,
    onProgressChange,
    handleOnDuration,
    handleOnProgress,
    handleOnEnded,
    handleOnPlay,
    handleOnPause,
    duration,
    forcePlay,
    play,
    isFullscreen,
    exitFullScreen,
    toggleFullScreen,
    enterFullScreen,
    pause,
  };
}
